import React, { useState, useEffect, useCallback } from 'react';
import {
  Phone, RefreshCw, Search, AlertTriangle, CheckCircle, XCircle, Clock,
  DollarSign, ArrowUpRight, ArrowDownLeft, Eye, ChevronDown, ChevronUp,
  MessageSquare, Activity, Info, Filter, Loader2, Send
} from 'lucide-react';
import {
  getTwilioAccount, getTwilioMessages, getTwilioMessageDetail,
  getTwilioErrors, getTwilioUsage, getTwilioPhoneInfo,
  type TwilioMessage, type TwilioAccountInfo, type TwilioUsageData,
} from '@/lib/api';

/* ─── Status badge helper ─── */
function StatusBadge({ status }: { status: string }) {
  const map: Record<string, { color: string; icon: React.ReactNode }> = {
    delivered: { color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', icon: <CheckCircle size={12} /> },
    read: { color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300', icon: <Eye size={12} /> },
    sent: { color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300', icon: <ArrowUpRight size={12} /> },
    queued: { color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300', icon: <Clock size={12} /> },
    sending: { color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300', icon: <Send size={12} /> },
    failed: { color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', icon: <XCircle size={12} /> },
    undelivered: { color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300', icon: <AlertTriangle size={12} /> },
    received: { color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300', icon: <ArrowDownLeft size={12} /> },
  };
  const s = map[status] || { color: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300', icon: <Info size={12} /> };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>
      {s.icon} {status}
    </span>
  );
}

/* ─── Direction badge ─── */
function DirectionBadge({ dir }: { dir: string }) {
  const isOut = dir?.includes('outbound');
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${isOut ? 'text-blue-600 dark:text-blue-400' : 'text-green-600 dark:text-green-400'}`}>
      {isOut ? <ArrowUpRight size={12} /> : <ArrowDownLeft size={12} />}
      {isOut ? 'Enviado' : 'Recibido'}
    </span>
  );
}

/* ─── Stat card ─── */
function StatCard({ label, value, sub, icon, color }: { label: string; value: string | number; sub?: string; icon: React.ReactNode; color: string }) {
  return (
    <div className={`rounded-xl border p-4 ${color}`}>
      <div className="flex items-center gap-2 mb-1 text-sm font-medium opacity-70">{icon} {label}</div>
      <div className="text-2xl font-bold">{value}</div>
      {sub && <div className="text-xs opacity-60 mt-1">{sub}</div>}
    </div>
  );
}

type Tab = 'messages' | 'errors' | 'usage' | 'account';

export function TwilioPanel() {
  const [tab, setTab] = useState<Tab>('messages');
  const [loading, setLoading] = useState(false);

  // Account
  const [account, setAccount] = useState<TwilioAccountInfo | null>(null);
  const [phoneInfo, setPhoneInfo] = useState<any>(null);

  // Messages
  const [messages, setMessages] = useState<TwilioMessage[]>([]);
  const [msgCount, setMsgCount] = useState(0);
  const [phoneFilter, setPhoneFilter] = useState('');
  const [dirFilter, setDirFilter] = useState<'' | 'inbound' | 'outbound'>('');
  const [statusFilter, setStatusFilter] = useState('');
  const [daysFilter, setDaysFilter] = useState(7);

  // Errors
  const [errors, setErrors] = useState<TwilioMessage[]>([]);
  const [errCount, setErrCount] = useState(0);

  // Usage
  const [usage, setUsage] = useState<TwilioUsageData | null>(null);

  // Detail
  const [detailMsg, setDetailMsg] = useState<any>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // ─── Load functions ─────────────────────────

  const loadAccount = useCallback(async () => {
    setLoading(true);
    try {
      const [acct, phone] = await Promise.all([getTwilioAccount(), getTwilioPhoneInfo()]);
      if (acct.success) setAccount(acct.data);
      if (phone.success) setPhoneInfo(phone.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const loadMessages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTwilioMessages({
        phone: phoneFilter || undefined,
        direction: dirFilter || undefined,
        status: statusFilter || undefined,
        days: daysFilter,
        limit: 50,
      });
      if (res.success && res.data) {
        setMessages(res.data.messages || []);
        setMsgCount(res.data.count || 0);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [phoneFilter, dirFilter, statusFilter, daysFilter]);

  const loadErrors = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTwilioErrors(7, 50);
      if (res.success && res.data) {
        setErrors(res.data.errors || []);
        setErrCount(res.data.count || 0);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const loadUsage = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTwilioUsage();
      if (res.success && res.data) setUsage(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  const loadDetail = async (sid: string) => {
    try {
      const res = await getTwilioMessageDetail(sid);
      if (res.success) {
        setDetailMsg(res.data);
        setDetailOpen(true);
      }
    } catch (e) { console.error(e); }
  };

  // Auto-load on tab change
  useEffect(() => {
    if (tab === 'messages') loadMessages();
    else if (tab === 'errors') loadErrors();
    else if (tab === 'usage') loadUsage();
    else if (tab === 'account') loadAccount();
  }, [tab]);

  // ─── Tab buttons ───────────────────────────

  const tabs: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: 'messages', label: 'Mensajes', icon: <MessageSquare size={16} /> },
    { key: 'errors', label: 'Errores', icon: <AlertTriangle size={16} /> },
    { key: 'usage', label: 'Uso & Costos', icon: <DollarSign size={16} /> },
    { key: 'account', label: 'Cuenta', icon: <Info size={16} /> },
  ];

  // ─── Render ─────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
            <Phone size={20} className="text-green-600 dark:text-green-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold">Twilio Dashboard</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Mensajes, delivery, errores y cuenta</p>
          </div>
        </div>
        <button
          onClick={() => {
            if (tab === 'messages') loadMessages();
            else if (tab === 'errors') loadErrors();
            else if (tab === 'usage') loadUsage();
            else loadAccount();
          }}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-sm transition"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Actualizar
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl">
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition ${
              tab === t.key
                ? 'bg-white dark:bg-gray-700 shadow-sm text-blue-600 dark:text-blue-400'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* Loading bar */}
      {loading && (
        <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
          <Loader2 size={14} className="animate-spin" /> Consultando Twilio...
        </div>
      )}

      {/* ──── Messages Tab ──── */}
      {tab === 'messages' && (
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl border">
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-gray-400" />
              <span className="text-xs font-medium text-gray-500">Filtros:</span>
            </div>
            <input
              type="text"
              placeholder="Teléfono (10 dígitos)"
              value={phoneFilter}
              onChange={e => setPhoneFilter(e.target.value)}
              className="px-3 py-1.5 text-sm rounded-lg border bg-white dark:bg-gray-900 dark:border-gray-700 w-44"
            />
            <select
              value={dirFilter}
              onChange={e => setDirFilter(e.target.value as any)}
              className="px-3 py-1.5 text-sm rounded-lg border bg-white dark:bg-gray-900 dark:border-gray-700"
            >
              <option value="">Todos</option>
              <option value="outbound">Enviados</option>
              <option value="inbound">Recibidos</option>
            </select>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="px-3 py-1.5 text-sm rounded-lg border bg-white dark:bg-gray-900 dark:border-gray-700"
            >
              <option value="">Cualquier status</option>
              <option value="delivered">Delivered</option>
              <option value="read">Read</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="undelivered">Undelivered</option>
            </select>
            <select
              value={daysFilter}
              onChange={e => setDaysFilter(Number(e.target.value))}
              className="px-3 py-1.5 text-sm rounded-lg border bg-white dark:bg-gray-900 dark:border-gray-700"
            >
              <option value={1}>Hoy</option>
              <option value={3}>3 días</option>
              <option value={7}>7 días</option>
              <option value={14}>14 días</option>
              <option value={30}>30 días</option>
            </select>
            <button
              onClick={loadMessages}
              disabled={loading}
              className="px-3 py-1.5 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition flex items-center gap-1"
            >
              <Search size={14} /> Buscar
            </button>
          </div>

          {/* Count badge */}
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {msgCount} mensaje{msgCount !== 1 ? 's' : ''} encontrado{msgCount !== 1 ? 's' : ''}
          </div>

          {/* Messages table */}
          <div className="overflow-x-auto rounded-xl border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800 text-left">
                  <th className="px-4 py-3 font-medium">Dirección</th>
                  <th className="px-4 py-3 font-medium">De / Para</th>
                  <th className="px-4 py-3 font-medium">Mensaje</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Fecha</th>
                  <th className="px-4 py-3 font-medium">Error</th>
                  <th className="px-4 py-3 font-medium w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y dark:divide-gray-700">
                {messages.map(msg => (
                  <tr key={msg.sid} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                    <td className="px-4 py-3"><DirectionBadge dir={msg.direction || ''} /></td>
                    <td className="px-4 py-3 font-mono text-xs max-w-[180px] truncate">
                      {msg.direction?.includes('outbound') ? msg.to : msg.from}
                    </td>
                    <td className="px-4 py-3 max-w-[300px] truncate text-gray-700 dark:text-gray-300">
                      {msg.body || <span className="italic text-gray-400">(media)</span>}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={msg.status || 'unknown'} /></td>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                      {msg.date_sent ? new Date(msg.date_sent).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-'}
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {msg.error_code ? (
                        <span className="text-red-600 dark:text-red-400" title={msg.error_message || ''}>
                          {msg.error_code}
                        </span>
                      ) : (
                        <span className="text-gray-300 dark:text-gray-600">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => loadDetail(msg.sid)}
                        className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                        title="Ver detalle"
                      >
                        <Eye size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {messages.length === 0 && !loading && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                      No se encontraron mensajes con estos filtros
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ──── Errors Tab ──── */}
      {tab === 'errors' && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <AlertTriangle size={16} className="text-red-500" />
            <span className="text-sm font-medium">{errCount} error{errCount !== 1 ? 'es' : ''} en los últimos 7 días</span>
          </div>

          {errors.length === 0 && !loading ? (
            <div className="p-8 text-center rounded-xl border">
              <CheckCircle size={40} className="mx-auto text-green-500 mb-3" />
              <p className="text-gray-500 dark:text-gray-400">¡Sin errores de entrega recientes!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {errors.map(err => (
                <div
                  key={err.sid}
                  className="p-4 rounded-xl border border-red-200 dark:border-red-800/30 bg-red-50/50 dark:bg-red-900/10 flex items-start gap-4"
                >
                  <XCircle size={18} className="text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <StatusBadge status={err.status || 'failed'} />
                      <span className="font-mono text-xs text-gray-500">{err.sid}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span>Para: <span className="font-mono">{err.to}</span></span>
                      <span>{err.date_sent ? new Date(err.date_sent).toLocaleString('es-MX') : '-'}</span>
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300 truncate">{err.body || '(sin texto)'}</p>
                    {err.error_code && (
                      <p className="text-xs text-red-600 dark:text-red-400 font-medium">
                        Error {err.error_code}: {err.error_message || 'Sin descripción'}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => loadDetail(err.sid)}
                    className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition"
                  >
                    <Eye size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ──── Usage Tab ──── */}
      {tab === 'usage' && usage && (
        <div className="space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard
              label="Mensajes SMS este mes"
              value={usage.total_sms_count}
              sub={usage.period}
              icon={<MessageSquare size={14} />}
              color="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/30 text-blue-900 dark:text-blue-100"
            />
            <StatCard
              label="Costo total (USD)"
              value={`$${usage.total_sms_cost_usd.toFixed(4)}`}
              sub="Solo categoría SMS"
              icon={<DollarSign size={14} />}
              color="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/30 text-green-900 dark:text-green-100"
            />
            <StatCard
              label="Categorías activas"
              value={usage.monthly_categories?.length || 0}
              sub="WhatsApp, SMS, Phone..."
              icon={<Activity size={14} />}
              color="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800/30 text-purple-900 dark:text-purple-100"
            />
          </div>

          {/* Daily breakdown */}
          {usage.daily_breakdown && usage.daily_breakdown.length > 0 && (
            <div className="rounded-xl border overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 font-medium text-sm">
                Desglose diario (últimos 7 días)
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b dark:border-gray-700">
                    <th className="px-4 py-2">Fecha</th>
                    <th className="px-4 py-2">Categoría</th>
                    <th className="px-4 py-2 text-right">Mensajes</th>
                    <th className="px-4 py-2 text-right">Costo (USD)</th>
                  </tr>
                </thead>
                <tbody className="divide-y dark:divide-gray-700">
                  {usage.daily_breakdown.map((d, i) => (
                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-2">{d.date}</td>
                      <td className="px-4 py-2 text-gray-500">{d.category}</td>
                      <td className="px-4 py-2 text-right font-mono">{d.count}</td>
                      <td className="px-4 py-2 text-right font-mono">${Number(d.price).toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Monthly categories */}
          {usage.monthly_categories && usage.monthly_categories.length > 0 && (
            <div className="rounded-xl border overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 font-medium text-sm">
                Categorías del mes
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left border-b dark:border-gray-700">
                    <th className="px-4 py-2">Categoría</th>
                    <th className="px-4 py-2">Descripción</th>
                    <th className="px-4 py-2 text-right">Cantidad</th>
                    <th className="px-4 py-2 text-right">Costo</th>
                  </tr>
                </thead>
                <tbody className="divide-y dark:divide-gray-700">
                  {usage.monthly_categories.map((c, i) => (
                    <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-2 font-mono text-xs">{c.category}</td>
                      <td className="px-4 py-2 text-gray-500">{c.description}</td>
                      <td className="px-4 py-2 text-right font-mono">{c.count}</td>
                      <td className="px-4 py-2 text-right font-mono">${Number(c.price || 0).toFixed(4)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ──── Account Tab ──── */}
      {tab === 'account' && (
        <div className="space-y-4">
          {account && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                label="Estado"
                value={account.status || '—'}
                icon={<Activity size={14} />}
                color={`border ${account.status === 'active' ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/30 text-green-900 dark:text-green-100' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-gray-100'}`}
              />
              <StatCard
                label="Balance"
                value={`$${account.balance || '—'}`}
                sub={account.currency || 'USD'}
                icon={<DollarSign size={14} />}
                color="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800/30 text-blue-900 dark:text-blue-100"
              />
              <StatCard
                label="Tipo de cuenta"
                value={account.type || '—'}
                icon={<Info size={14} />}
                color="bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800/30 text-purple-900 dark:text-purple-100"
              />
              <StatCard
                label="Número WhatsApp"
                value={account.whatsapp_number || '—'}
                icon={<Phone size={14} />}
                color="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800/30 text-green-900 dark:text-green-100"
              />
            </div>
          )}

          {account && (
            <div className="rounded-xl border overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 font-medium text-sm">
                Detalle de la cuenta
              </div>
              <div className="p-4 space-y-3">
                <InfoRow label="Account SID" value={account.account_sid} mono />
                <InfoRow label="Nombre" value={account.friendly_name} />
                <InfoRow label="Creada" value={account.date_created} />
                <InfoRow label="WhatsApp From" value={account.whatsapp_number} mono />
              </div>
            </div>
          )}

          {phoneInfo && (
            <div className="rounded-xl border overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800 font-medium text-sm">
                Número de teléfono
              </div>
              <div className="p-4 space-y-3">
                <InfoRow label="Configurado" value={phoneInfo.configured_number} mono />
                <InfoRow label="Número limpio" value={phoneInfo.phone_number_clean} mono />
                {phoneInfo.phone_details && typeof phoneInfo.phone_details === 'object' ? (
                  <>
                    <InfoRow label="SID" value={phoneInfo.phone_details.sid} mono />
                    <InfoRow label="Friendly Name" value={phoneInfo.phone_details.friendly_name} />
                    <InfoRow label="Status" value={phoneInfo.phone_details.status} />
                    <InfoRow label="SMS URL" value={phoneInfo.phone_details.sms_url} mono />
                  </>
                ) : (
                  <InfoRow label="Nota" value={phoneInfo.note || 'WhatsApp Sandbox'} />
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ──── Message Detail Modal ──── */}
      {detailOpen && detailMsg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setDetailOpen(false)}>
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl border shadow-xl max-w-lg w-full mx-4 max-h-[80vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b dark:border-gray-700">
              <h3 className="font-bold text-lg">Detalle del mensaje</h3>
              <button onClick={() => setDetailOpen(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
                <XCircle size={18} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              <InfoRow label="SID" value={detailMsg.sid} mono />
              <InfoRow label="De" value={detailMsg.from} mono />
              <InfoRow label="Para" value={detailMsg.to} mono />
              <InfoRow label="Status" value={detailMsg.status} badge />
              <InfoRow label="Dirección" value={detailMsg.direction} />
              <div className="border-t dark:border-gray-700 pt-3">
                <span className="text-xs font-medium text-gray-500 block mb-1">Contenido:</span>
                <p className="text-sm bg-gray-50 dark:bg-gray-800 rounded-lg p-3 whitespace-pre-wrap">
                  {detailMsg.body || '(sin texto)'}
                </p>
              </div>
              <InfoRow label="Fecha creación" value={detailMsg.date_created} />
              <InfoRow label="Fecha envío" value={detailMsg.date_sent} />
              <InfoRow label="Última actualización" value={detailMsg.date_updated} />
              <InfoRow label="Precio" value={detailMsg.price ? `$${detailMsg.price} ${detailMsg.price_unit || 'USD'}` : '—'} />
              <InfoRow label="Segmentos" value={detailMsg.num_segments} />
              <InfoRow label="Media" value={detailMsg.num_media} />
              {detailMsg.error_code && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800/30">
                  <span className="text-xs font-medium text-red-600 dark:text-red-400">
                    Error {detailMsg.error_code}: {detailMsg.error_message || 'Sin descripción'}
                  </span>
                </div>
              )}
              {detailMsg.messaging_service_sid && (
                <InfoRow label="Messaging Service" value={detailMsg.messaging_service_sid} mono />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Small info row component ─── */
function InfoRow({ label, value, mono, badge }: { label: string; value?: string | number | null; mono?: boolean; badge?: boolean }) {
  if (!value && value !== 0) return null;
  return (
    <div className="flex items-start gap-3">
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-32 flex-shrink-0 pt-0.5">{label}:</span>
      {badge ? (
        <StatusBadge status={String(value)} />
      ) : (
        <span className={`text-sm break-all ${mono ? 'font-mono text-xs bg-gray-100 dark:bg-gray-800 px-2 py-0.5 rounded' : ''}`}>
          {String(value)}
        </span>
      )}
    </div>
  );
}

export default TwilioPanel;
